import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, MessageCircle, Heart } from 'lucide-react';
import { BRAND, waLink } from '../lib/constants';
import { useRegistrationUrl } from '../lib/useSettings';

const internalLinks = [
  { to: '/about', label: 'About Us' },
  { to: '/artists', label: 'Discover Artists' },
  { to: '/careers', label: 'Careers' },
  { to: '/blog', label: 'Journal' },
];

export const Navbar = ({ topOffset = 0 }) => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const registrationUrl = useRegistrationUrl();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <header
      data-testid="site-navbar"
      style={{ top: topOffset }}
      className={`fixed left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md border-b border-[#eaeaea]'
          : 'bg-white/80 backdrop-blur-sm'
      }`}
    >
      <div className="px-5 md:px-8 lg:px-10 h-20 flex items-center justify-between gap-4">
        <Link to="/" data-testid="navbar-logo" className="flex items-center gap-3 shrink-0">
          <img src={BRAND.logo} alt="FindYourMUA" className="h-10 w-10 object-cover rounded-full" />
          <div className="hidden sm:block leading-tight">
            <div className="font-serif text-lg tracking-tight">FindYour<span className="text-[#eb5381]">MUA</span></div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">Verified Beauty Artists</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-8 mx-auto">
          <NavLink
            to="/about"
            data-testid="nav-link-about"
            className={({ isActive }) =>
              `text-[11px] uppercase tracking-[0.2em] transition-colors whitespace-nowrap ${
                isActive ? 'text-[#eb5381]' : 'text-neutral-800 hover:text-[#eb5381]'
              }`
            }
          >
            About Us
          </NavLink>
          <NavLink
            to="/artists"
            data-testid="nav-link-artists"
            className={({ isActive }) =>
              `text-[11px] uppercase tracking-[0.2em] transition-colors whitespace-nowrap ${
                isActive ? 'text-[#eb5381]' : 'text-neutral-800 hover:text-[#eb5381]'
              }`
            }
          >
            Discover Artists
          </NavLink>
          <a
            href="/register"
            data-testid="nav-link-register-mua"
            className="text-[11px] uppercase tracking-[0.2em] transition-colors whitespace-nowrap text-neutral-800 hover:text-[#eb5381]"
          >
            Register as Artist
          </a>
          <NavLink
            to="/careers"
            data-testid="nav-link-careers"
            className={({ isActive }) =>
              `text-[11px] uppercase tracking-[0.2em] transition-colors whitespace-nowrap ${
                isActive ? 'text-[#eb5381]' : 'text-neutral-800 hover:text-[#eb5381]'
              }`
            }
          >
            Careers
          </NavLink>
          <NavLink
            to="/blog"
            data-testid="nav-link-blog"
            className={({ isActive }) =>
              `text-[11px] uppercase tracking-[0.2em] transition-colors whitespace-nowrap ${
                isActive ? 'text-[#eb5381]' : 'text-neutral-800 hover:text-[#eb5381]'
              }`
            }
          >
            Journal
          </NavLink>
        </nav>

        <div className="flex items-center gap-2 shrink-0">
          <Link
            to="/wishlist"
            data-testid="navbar-wishlist-link"
            aria-label="Wishlist"
            className="w-10 h-10 grid place-items-center border border-transparent hover:border-[#eb5381] hover:text-[#eb5381] transition-colors"
          >
            <Heart className="w-4 h-4" />
          </Link>
          <Link
            to="/contact"
            data-testid="navbar-skip-search-cta"
            className="hidden xl:inline-flex items-center gap-2 border border-black hover:bg-black hover:text-white px-4 py-3 text-[10px] uppercase tracking-[0.2em] transition-colors whitespace-nowrap"
          >
            Skip The Search
          </Link>
          <a
            href={waLink(`Hi ${BRAND.name}, I'd like to book a makeup artist.`)}
            target="_blank"
            rel="noreferrer"
            data-testid="navbar-whatsapp-cta"
            className="hidden md:inline-flex items-center gap-2 bg-[#eb5381] hover:bg-[#d64570] text-white px-4 py-3 text-[10px] uppercase tracking-[0.2em] transition-colors whitespace-nowrap"
          >
            <MessageCircle className="w-4 h-4" />
            <span className="hidden xl:inline">Book on WhatsApp</span>
            <span className="xl:hidden">Book</span>
          </a>
          <button
            className="lg:hidden p-2 -mr-2"
            onClick={() => setOpen(o => !o)}
            aria-label="Open menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden bg-white border-t border-[#eaeaea]" data-testid="mobile-menu-panel">
          <div className="container-edge py-8 flex flex-col gap-6">
            {internalLinks.map(l => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-${l.to.replace('/', '')}`}
                className="font-serif text-2xl text-neutral-900 hover:text-[#eb5381]"
              >
                {l.label}
              </Link>
            ))}
            <a
              href="/register"
              data-testid="mobile-nav-register-mua"
              className="font-serif text-2xl text-neutral-900 hover:text-[#eb5381]"
            >
              Register as Artist
            </a>
            <Link
              to="/contact"
              data-testid="mobile-skip-search-cta"
              className="border border-black text-black px-6 py-4 text-[12px] uppercase tracking-[0.2em] text-center"
            >
              Skip The Search
            </Link>
            <a
              href={waLink('Hi FindYourMUA, I\'d like to book.')}
              target="_blank"
              rel="noreferrer"
              className="bg-[#eb5381] text-white px-6 py-4 text-[12px] uppercase tracking-[0.2em] text-center"
              data-testid="mobile-whatsapp-cta"
            >
              Book on WhatsApp
            </a>
          </div>
        </div>
      )}
    </header>
  );
};
